/**
 * AccountLogin.js
 *
 * Raider Depot — Adult Account Login
 * Route: /login
 *
 * Returning adults sign in with email/password.
 * On success: shows navigation menu (Place Order, My Orders, Change Password, Sign Out).
 */

import React, { useState, useEffect } from "react";
import { auth } from "../firebase";
import {
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  signOut,
} from "firebase/auth";
import NavBar from "../components/NavBar";

// ── Constants ───────────────────────────────────────────────────────
const ACCOUNT_API_URL = "/api/accounts";
const TOKEN_STORAGE_KEY = "raider-depot-token";

// ── Styles ──────────────────────────────────────────────────────────
const styles = {
  container: {
    minHeight: "100vh",
    backgroundColor: "#f0f4f8",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "24px 16px",
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  card: {
    backgroundColor: "#ffffff",
    borderRadius: "16px",
    boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
    padding: "32px 28px",
    maxWidth: "440px",
    width: "100%",
  },
  title: {
    fontSize: "26px",
    fontWeight: "700",
    color: "#1a365d",
    textAlign: "center",
    margin: "0 0 4px 0",
  },
  subtitle: {
    fontSize: "14px",
    color: "#64748b",
    textAlign: "center",
    margin: "0 0 24px 0",
  },
  label: {
    display: "block",
    fontSize: "13px",
    fontWeight: "600",
    color: "#475569",
    marginBottom: "6px",
    marginTop: "16px",
  },
  input: {
    width: "100%",
    padding: "12px 14px",
    fontSize: "15px",
    border: "2px solid #e2e8f0",
    borderRadius: "10px",
    outline: "none",
    boxSizing: "border-box",
    transition: "border-color 0.2s",
  },
  buttonPrimary: {
    width: "100%",
    boxSizing: "border-box",
    padding: "14px",
    fontSize: "16px",
    fontWeight: "600",
    color: "#ffffff",
    backgroundColor: "#1A3B6E",
    border: "none",
    borderRadius: "10px",
    cursor: "pointer",
    marginTop: "12px",
    textAlign: "left",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  buttonSecondary: {
    width: "100%",
    boxSizing: "border-box",
    padding: "14px",
    fontSize: "15px",
    fontWeight: "500",
    color: "#1A3B6E",
    backgroundColor: "#E8F2FB",
    border: "none",
    borderRadius: "10px",
    cursor: "pointer",
    marginTop: "10px",
    textAlign: "left",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  buttonDanger: {
    width: "100%",
    boxSizing: "border-box",
    padding: "14px",
    fontSize: "15px",
    fontWeight: "500",
    color: "#dc2626",
    backgroundColor: "#fef2f2",
    border: "none",
    borderRadius: "10px",
    cursor: "pointer",
    marginTop: "10px",
    textAlign: "left",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  buttonSignIn: {
    width: "100%",
    padding: "14px",
    fontSize: "16px",
    fontWeight: "600",
    color: "#ffffff",
    backgroundColor: "#3b82f6",
    border: "none",
    borderRadius: "10px",
    cursor: "pointer",
    marginTop: "24px",
  },
  buttonDisabled: {
    backgroundColor: "#94a3b8",
    cursor: "not-allowed",
  },
  errorText: {
    color: "#ef4444",
    fontSize: "13px",
    marginTop: "8px",
  },
  successText: {
    color: "#16a34a",
    fontSize: "13px",
    marginTop: "8px",
  },
  linkButton: {
    background: "none",
    border: "none",
    color: "#3b82f6",
    fontSize: "13px",
    cursor: "pointer",
    textDecoration: "underline",
    padding: 0,
  },
  divider: {
    borderTop: "1px solid #e2e8f0",
    margin: "20px 0",
  },
};

// ── Component ───────────────────────────────────────────────────────
export default function AccountLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [loggedIn, setLoggedIn] = useState(false);
  const [tokenId, setTokenId] = useState(null);

  useEffect(() => {
    if (loggedIn) window.scrollTo(0, 0);
  }, [loggedIn]);

  const handleLogin = async () => {
    if (!email || !password) {
      setError("Please enter your email and password.");
      return;
    }

    setError("");
    setLoading(true);

    try {
      // 1. Sign in with Firebase
      const cred = await signInWithEmailAndPassword(auth, email, password);
      const user = cred.user;

      // 2. Fetch linked token from accounts API
      const idToken = await user.getIdToken();
      const res = await fetch(`${ACCOUNT_API_URL}/me`, {
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
      });

      if (res.ok) {
        const data = await res.json();
        if (data.tokenId) {
          try {
            localStorage.setItem(TOKEN_STORAGE_KEY, data.tokenId);
          } catch (e) {
            // no-op
          }
          setTokenId(data.tokenId);
        }
      }

      // 3. Show nav menu instead of redirecting
      setLoggedIn(true);
    } catch (err) {
      if (
        err.code === "auth/user-not-found" ||
        err.code === "auth/wrong-password" ||
        err.code === "auth/invalid-credential"
      ) {
        setError("Invalid email or password. Please try again.");
      } else if (err.code === "auth/too-many-requests") {
        setError(
          "Too many failed attempts. Please wait a few minutes and try again."
        );
      } else {
        setError("Sign in failed. Please try again.");
        console.error("Login error:", err);
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async () => {
    if (!email) {
      setError("Please enter your email address first, then click reset.");
      return;
    }

    try {
      await sendPasswordResetEmail(auth, email);
      setResetSent(true);
      setError("");
    } catch (err) {
      // Don't reveal whether email exists
      setResetSent(true);
    }
  };

  const handleSignOut = async () => {
    await signOut(auth);
    window.location.href = "/";
  };

  const handleChangePassword = async () => {
    try {
      await sendPasswordResetEmail(auth, email);
      setResetSent(true);
    } catch (err) {
      // Don't reveal whether email exists
      setResetSent(true);
    }
  };

  // ── Post-login nav menu ──────────────────────────────────────────
  if (loggedIn) {
    return (
      <div style={styles.container}>
        <NavBar title="Sign In" />
        <div style={styles.card}>
          <h1 style={styles.title}>Welcome back!</h1>
          {tokenId && (
            <p style={{ ...styles.subtitle, marginBottom: 8 }}>
              Token: <strong style={{ color: "#1A3B6E" }}>{tokenId}</strong>
            </p>
          )}
          <p style={styles.subtitle}>What would you like to do?</p>

          <a
            href="/order"
            style={{ ...styles.buttonPrimary, textDecoration: "none" }}
          >
            <span>Place an Order</span>
            <span>→</span>
          </a>

          <a
            href="/my-orders"
            style={{ ...styles.buttonSecondary, textDecoration: "none" }}
          >
            <span>My Orders</span>
            <span>→</span>
          </a>

          <button style={styles.buttonSecondary} onClick={handleChangePassword}>
            <span>Change Password</span>
            <span>→</span>
          </button>

          {resetSent && (
            <p style={styles.successText}>
              Password reset email sent. Check your inbox.
            </p>
          )}

          <button style={styles.buttonDanger} onClick={handleSignOut}>
            <span>Sign Out</span>
            <span>→</span>
          </button>
        </div>
      </div>
    );
  }

  // ── Login form ───────────────────────────────────────────────────
  return (
    <div style={styles.container}>
      <NavBar title="Sign In" />
      <div style={styles.card}>
        <h1 style={styles.title}>Sign In</h1>
        <p style={styles.subtitle}>
          Sign in to your Raider Depot account to place an order.
        </p>

        <label style={styles.label}>Email Address</label>
        <input
          style={styles.input}
          type="email"
          placeholder="your.email@example.com"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            setError("");
            setResetSent(false);
          }}
          autoComplete="email"
          autoFocus
        />

        <label style={styles.label}>Password</label>
        <input
          style={styles.input}
          type="password"
          placeholder="••••••••"
          value={password}
          onChange={(e) => {
            setPassword(e.target.value);
            setError("");
          }}
          onKeyDown={(e) => e.key === "Enter" && handleLogin()}
          autoComplete="current-password"
        />

        <div style={{ textAlign: "right", marginTop: "8px" }}>
          <button style={styles.linkButton} onClick={handlePasswordReset}>
            Forgot password?
          </button>
        </div>

        {error && <p style={styles.errorText}>{error}</p>}
        {resetSent && (
          <p style={styles.successText}>
            If an account exists with that email, a password reset link has been sent.
          </p>
        )}

        <button
          style={{
            ...styles.buttonSignIn,
            ...(loading ? styles.buttonDisabled : {}),
          }}
          onClick={handleLogin}
          disabled={loading}
        >
          {loading ? "Signing in..." : "Sign In"}
        </button>

        <div style={styles.divider} />

        <div style={{ textAlign: "center" }}>
          <span style={{ fontSize: "13px", color: "#94a3b8" }}>
            Don't have an account?{" "}
          </span>
          <a href="/account" style={{ fontSize: "13px", color: "#3b82f6" }}>
            Create one
          </a>
        </div>

        <div style={{ textAlign: "center", marginTop: "8px" }}>
          <span style={{ fontSize: "13px", color: "#94a3b8" }}>
            Just want to order?{" "}
          </span>
          <a href="/order" style={{ fontSize: "13px", color: "#3b82f6" }}>
            Use token only
          </a>
        </div>
      </div>
    </div>
  );
}
